import React from 'react'

import { AIGenerated2 } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, func, number, string } from 'prop-types'

import { NOOP } from 'Utils/stableDefaults'

import Benefit from './Components/Benefit/Benefit'
import TrialCta from './Components/TrialCta/TrialCta'

const STATIC_BENEFITS = [
  'Edit and enhance your own or purchased 3D models',
  'All top AI engines included: Tripo, Hunyuan, Meshy',
]

// The card always shows the subscription and its primary "Start Subscription" CTA. The flags add
// the optional pieces UpgradeHeader maps each access tier to:
//   showStudioButton  -- "Go to CGTrader AI" outline CTA (users who can already generate)
//   showTrialBlock    -- the free-generations block (eligible_for_trial and must_join_waitlist)
//   showOriginalPrice -- struck-through general price (eligible_for_trial only)
//   showWaitlistCta   -- swaps the trial block's "Try Free Generations" link for the waitlist
//                        actions, which open the waitlist modal via onJoinWaitlist (must_join_waitlist)
// price/originalPrice/monthlyModels come from pageConfig (server-sourced from the Subscription model);
// the defaults are the pre-hydration / critical-CSS fallbacks.
function SubscriptionCard({
  showStudioButton = false,
  showTrialBlock = false,
  showOriginalPrice = false,
  showWaitlistCta = false,
  subscriptionHref,
  studioHref,
  price = '$9.99',
  originalPrice = '$24.99',
  monthlyModels = 25,
  onJoinWaitlist = NOOP,
  onTryFreeGenerations = null,
}) {
  const benefits = [
    `${monthlyModels} monthly CGTrader AI generations or Marketplace downloads`,
    ...STATIC_BENEFITS,
  ]

  return (
    <div className="ai-sub-landing__card">
      <div className="ai-sub-landing__card-body">
        <div className="ai-sub-landing__card-header">
          <h2 className="ai-sub-landing__card-title cgt-heading-400">CGTrader Subscription</h2>
          <p className="ai-sub-landing__card-price">
            {showOriginalPrice && <span className="ai-sub-landing__card-price-original">{originalPrice}</span>}
            <span className="ai-sub-landing__card-price-amount">{price}</span>
            <span className="ai-sub-landing__card-price-period">/month</span>
          </p>
        </div>
        <ul className="ai-sub-landing__benefits">
          {benefits.map((benefit) => <Benefit key={benefit}>{benefit}</Benefit>)}
        </ul>
        <div className="ai-sub-landing__card-actions">
          <a className="ai-sub-landing__cta ai-sub-landing__cta--primary" href={subscriptionHref}>
            Start Subscription
          </a>
          {showStudioButton && (
            <a className="ai-sub-landing__cta ai-sub-landing__cta--outline" href={studioHref}>
              Go to CGTrader AI
            </a>
          )}
        </div>
      </div>
      {showTrialBlock && (
        <div className="ai-sub-landing__trial">
          <span className="ai-sub-landing__card-divider" aria-hidden="true" />
          <p className="ai-sub-landing__trial-text">
            <span className="ai-sub-landing__trial-title">5 free AI generations available</span>{' '}
            <span className="ai-sub-landing__trial-desc">
              - perfect for getting started and trying things out.
            </span>
          </p>
          <ul className="ai-sub-landing__trial-notes">
            <Benefit icon={AIGenerated2} iconClassName="ai-sub-landing__trial-note-icon">
              No credit card required
            </Benefit>
          </ul>
          {showWaitlistCta ? (
            <div className="ai-sub-landing__waitlist-actions">
              <button
                type="button"
                className="ai-sub-landing__cta ai-sub-landing__cta--outline"
                onClick={onJoinWaitlist}
              >
                Join the Waitlist
              </button>
              <button
                type="button"
                className="ai-sub-landing__waitlist-link cgt-body-md-semibold"
                onClick={onJoinWaitlist}
              >
                I have access code
              </button>
            </div>
          ) : (
            <TrialCta studioHref={studioHref} onTryFreeGenerations={onTryFreeGenerations} />
          )}
        </div>
      )}
    </div>
  )
}

SubscriptionCard.propTypes = {
  showStudioButton: bool,
  showTrialBlock: bool,
  showOriginalPrice: bool,
  showWaitlistCta: bool,
  subscriptionHref: string.isRequired,
  studioHref: string.isRequired,
  price: string,
  originalPrice: string,
  monthlyModels: number,
  onJoinWaitlist: func,
  onTryFreeGenerations: func,
}

export default SubscriptionCard
